import { Component, inject, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { MatTableModule } from "@angular/material/table";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { AgGridAngular } from "@ag-grid-community/angular";
import { ColDef, GridOptions, ICellRendererParams, IRowNode, ValueGetterParams } from "@ag-grid-community/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, Validators } from "@angular/forms";
import { AccessLevel, AuthorizedAccess, OrganizationUser, UserApi } from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { AuthService, OrganizationUsersService } from "@front/m19-services";
import {
  IAlertComponent,
  IBadgeComponent,
  IButtonComponent,
  IInputComponent,
  IRadioComponent,
  ISelectComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  Option,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActionButtonsComponent } from "@m19-board/insights/advertising-stats/action-buttons/action-buttons.component";
import { accessHeaderName } from "@m19-board/settings/user-management/organization-card/manage-authorized-user-modal/manage-authorized-access-modal.component";
import { ICON_SEARCH, ICON_TRASH_O } from "@m19-board/utils/iconsLabels";
import { catchError, debounceTime, distinctUntilChanged, filter, map, Observable, of, switchMap } from "rxjs";

interface AuthorizedUser {
  userName: string;
  email: string;
  accessLevel: AccessLevel;
  isPending: boolean;
  lastInvite?: string;
}

@UntilDestroy()
@Component({
  templateUrl: "./manage-authorized-users-modal.component.html",
  styleUrls: ["manage-authorized-users-modal.component.scss"],
  standalone: true,
  imports: [
    TranslocoDirective,
    MatTableModule,
    IButtonComponent,
    IInputComponent,
    ISelectComponent,
    AgGridAngular,
    IAlertComponent,
  ],
})
export class ManageAuthorizedUsersModalComponent implements OnInit {
  readonly AccessLevel = AccessLevel;
  accessLevel = AccessLevel.READ;

  private readonly translocoService = inject(TranslocoService);
  private readonly userApi = inject(UserApi);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly organizationUsersService = inject(OrganizationUsersService);
  private readonly toasterService = inject(ToasterService);

  modalData = inject(MODAL_DATA) as ManageAuthorizedUsersModalData;
  modalRef = inject(ModalRef);

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular<AuthorizedUser>;

  @ViewChild("newUserModal") newUserModal!: TemplateRef<any>;
  newUserModalRef: ModalRef<void> | null = null;

  authorizedAccess?: AuthorizedAccess[];

  private readonly loggedUser = toSignal(this.authService.loggedUser$);
  tableFilter = signal<string | null>(null);
  callLoading = signal(false);
  emailControl = new FormControl("", { validators: [Validators.required, Validators.email], nonNullable: true });
  private readonly emailValue$: Observable<string | null> = this.emailControl.valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    filter((_) => this.emailControl.valid && this.emailControl.value !== null),
  );

  emailValue = toSignal<string | null>(this.emailValue$);

  isEmailRegistered = toSignal(
    this.emailValue$.pipe(
      switchMap((email) => {
        if (!email) return of(true);
        return this.userApi.doesUserExist({ email }).pipe(
          map((_) => true),
          catchError((_) => of(false)),
        );
      }),
    ),
  );

  private accessColDefs = (): ColDef<AuthorizedUser>[] => {
    return [AccessLevel.STATS_ONLY, AccessLevel.READ, AccessLevel.WRITE].map((accessLevel) => ({
      field: "accessLevel",
      cellClass: "flex items-center ",
      headerName: accessHeaderName[accessLevel as keyof typeof accessHeaderName],
      valueGetter: (params: ValueGetterParams<AuthorizedUser, any>) => {
        return params.data && params.data?.accessLevel === accessLevel;
      },
      sort: "desc",
      sortIndex: accessLevel === AccessLevel.WRITE ? 0 : accessLevel === AccessLevel.READ ? 1 : 2,
      cellRendererSelector: (params: ICellRendererParams<AuthorizedUser, boolean>) => {
        return {
          component: IRadioComponent,
          params: {
            checked: params.value,
            onClick: () => {
              this.setAccessLevel(params.data!.email!, accessLevel);
            },
          },
        };
      },
    }));
  };

  gridData = signal<AuthorizedUser[]>([]);

  gridOptions: GridOptions<AuthorizedUser> = {
    ...gridConfig({}),
    sideBar: false,
    pagination: true,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      cellClass: "flex items-center",
    },
    columnDefs: [
      {
        field: "userName",
        headerName: this.translocoService.translate("common.name"),
      },
      {
        field: "email",
        headerName: this.translocoService.translate("common.email"),
      },
      {
        field: "isPending",
        headerName: "Status",
        width: 120,
        valueGetter: (params: ValueGetterParams<AuthorizedUser>) => {
          return this.translocoService
            .translate(params.data?.isPending ? "user-management.pending" : "user-management.accepted")
            .toUpperCase();
        },
        valueFormatter: (_) => {
          return ""; // hide value if user is not pending
        },
        filter: "agSetColumnFilter",
        cellRendererSelector: (params: ICellRendererParams<AuthorizedUser>) => {
          if (params.data?.isPending) {
            const lastInviteDate = new Date(params.data!.lastInvite!);
            const lastInviteDateString = lastInviteDate.toLocaleDateString(this.loggedUser()?.locale ?? "en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
            return {
              component: IBadgeComponent,
              params: {
                icon: "icon-[lucide--mail]",
                label: this.translocoService.translate("user-management.pending").toUpperCase(),
                color: "orange",
                size: "sm",
                variant: "soft",
                tooltipValue: this.translocoService.translate("user-management.last_invite_sent", [
                  lastInviteDateString,
                ]),
              },
            };
          }
          return undefined;
        },
      },
      ...this.accessColDefs(),
      {
        cellRendererSelector: (params: ICellRendererParams<AuthorizedUser, boolean>) => {
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: [
                {
                  icon: "icon-[lucide--x]",
                  tooltip: this.translocoService.translate("manage-authorized-users-modal.revoke_access"),
                  onClick: () => this.revokeAccess(params.data!.email!),
                },
              ],
            },
          };
        },
      },
    ],
  };

  accessLevelOptions: Option<AccessLevel>[] = [
    {
      label: this.translocoService.translate("manage-authorized-users-modal.360_dashboards_access"),
      value: AccessLevel.STATS_ONLY,
    },
    {
      label: this.translocoService.translate("manage-authorized-access-modal.read_only_access"),
      value: AccessLevel.READ,
    },
    {
      label: this.translocoService.translate("manage-authorized-access-modal.read_write_access"),
      value: AccessLevel.WRITE,
    },
  ];

  ngOnInit(): void {
    this.organizationUsersService
      .listAuthorizedAccess()
      .pipe(untilDestroyed(this))
      .subscribe((access) => {
        this.authorizedAccess = access;
        this.gridData.set(
          access
            .filter(
              (x) => x.profileId === this.modalData.profileId && x.organizationId === this.modalData.organizationId,
            )
            .map((x) => ({
              userName: x.userName!,
              email: x.email!,
              accessLevel: x.accessLevel!,
              isPending: !!x.invitedBy && !x.registered,
              lastInvite: x.lastInvite,
            })),
        );
      });
  }

  openNewUserModal(): void {
    this.newUserModalRef = this.modalService.openModal(this.newUserModal, {
      modalTitle: this.translocoService.translate("manage-authorized-users-modal.add_user"),
    });
  }

  setAccessLevelNewUser(accessLevel: AccessLevel) {
    this.accessLevel = accessLevel;
  }

  addNewUser(): void {
    const email = this.emailValue();
    if (!email) return;
    if (this.checkUserAlreadyAuthorized(email)) {
      this.toasterService.error("User already authorized", "User already authorized");
    } else if (this.isEmailRegistered()) {
      this.addAuthorizedUser();
    } else {
      this.sendInvitation(email);
    }
  }

  private sendInvitation(email: string): void {
    this.callLoading.set(true);
    this.userApi
      .registerUser({
        userBase: {
          email: email,
          invitedBy: this.loggedUser()?.userId,
        },
      })
      .pipe(
        switchMap((_) => this.addAuthorizedUserAccess(email)),
        switchMap((_) =>
          this.userApi.sendUserInvitation({
            email: email,
            domain: window.location.hostname,
            organizationId: this.modalData.organizationId,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("user-management.success_invite_sent", [email]));
          this.newUserModalRef?.close();
          this.callLoading.set(false);
        },
        error: (error) => {
          this.toasterService.error(error);
          this.callLoading.set(false);
        },
      });
  }

  private checkUserAlreadyAuthorized(email: string): boolean {
    return this.authorizedAccess?.some((x) => x.email === email) ?? false;
  }

  private addAuthorizedUserAccess(email: string): Observable<void> {
    return this.organizationUsersService.addAuthorizedAccessAsync([
      {
        profileId: this.modalData.profileId,
        organizationId: this.modalData.organizationId,
        email: email,
        accessLevel: this.accessLevel,
      },
    ]);
  }

  addAuthorizedUser(): void {
    this.addAuthorizedUserAccess(this.emailValue()!).subscribe({
      next: () => {
        this.toasterService.success(
          "Successfully authorized " +
            this.emailControl.value +
            " to access " +
            this.modalData.accountName +
            " - " +
            this.modalData.marketplace,
          "User authorization updated",
        );
        this.newUserModalRef?.close();
      },
      error: (error) => {
        this.toasterService.error(error, "User authorization update error");
      },
    });
  }

  setAccessLevel(email: string, accessLevel: AccessLevel): void {
    this.organizationUsersService
      .addAuthorizedAccessAsync([
        {
          profileId: this.modalData.profileId,
          organizationId: this.modalData.organizationId,
          email,
          accessLevel,
        },
      ])
      .subscribe({
        next: () => {
          this.toasterService.success(
            "Successfully updated access of " +
              email +
              " to " +
              this.modalData.accountName +
              " - " +
              this.modalData.marketplace,
            "User authorization updated",
          );
        },
        error: (error) => this.toasterService.error(error, "User authorization update error"),
      });
  }

  revokeAccess(email: string): void {
    const authorizedAccess: AuthorizedAccess = {
      organizationId: this.modalData.organizationId,
      profileId: this.modalData.profileId,
      email: email,
    };
    this.organizationUsersService.revokeAuthorizedAccessAsync([authorizedAccess]).subscribe({
      next: () => {
        this.toasterService.success(
          "Successfully revoked access from " +
            email +
            " to " +
            this.modalData.accountName +
            " - " +
            this.modalData.marketplace,
          "Access has been revoked",
        );
      },
      error: (error) => this.toasterService.error(error, "User authorization update error"),
    });
  }

  close(): void {
    this.modalRef.close();
  }

  isExternalFilterPresent = (): boolean => {
    return !!this.tableFilter();
  };

  doesExternalFilterPass = (node: IRowNode<AuthorizedUser>): boolean => {
    const email = node.data?.email;
    const userName = node.data?.userName;
    return !!(
      (email && email.toLowerCase().includes(this.tableFilter()?.toLowerCase() ?? "")) ||
      (userName && userName.toLowerCase().includes(this.tableFilter()?.toLowerCase() ?? ""))
    );
  };

  externalFilterChanged(newValue: string): void {
    this.tableFilter.set(newValue);
    this.agGrid.api?.onFilterChanged();
  }

  protected readonly ICON_SEARCH = ICON_SEARCH;
  protected readonly ICON_TRASH_O = ICON_TRASH_O;
}

export interface ManageAuthorizedUsersModalData {
  organizationId: number;
  profileId: number;
  accountName: string;
  marketplace: string;
}
